import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CoverImage } from '../components/CoverImage';
import type { SeriesDetail, SeriesFormData, Novel } from '../services/types';
import { SeriesEditorModal } from '../components/admin/SeriesEditorModal';
import { AddNovelToSeriesModal } from '../components/admin/AddNovelToSeriesModal';

export function SeriesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [series, setSeries] = useState<SeriesDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal 狀態
  const [showEditor, setShowEditor] = useState(false);
  const [showAddNovel, setShowAddNovel] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 拖拽狀態
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // 載入系列詳情
  const loadSeries = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getSeriesDetail(id);
      setSeries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  // 更新系列
  const handleSaveSeries = async (data: SeriesFormData) => {
    if (!id) return;
    const updated = await api.updateSeries(id, data);
    setSeries(prev => prev ? { ...prev, ...updated } : null);
  };

  // 刪除系列
  const handleDeleteSeries = async () => {
    if (!id || !series) return;

    const confirmed = window.confirm(`確定要刪除系列「${series.title}」嗎？\n系列內的小說不會被刪除。`);
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await api.deleteSeries(id);
      navigate('/series', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '刪除失敗');
      setIsDeleting(false);
    }
  };

  // 加入小說
  const handleAddNovel = async (novelId: string) => {
    if (!id) return;
    await api.addNovelToSeries(id, novelId);
    await loadSeries();
  };

  // 移除小說
  const handleRemoveNovel = async (novelId: string) => {
    if (!id || !series) return;

    const novel = series.novels.find(n => n.id === novelId);
    const confirmed = window.confirm(`確定要從系列中移除「${novel?.title || '此小說'}」嗎？`);
    if (!confirmed) return;

    try {
      await api.removeNovelFromSeries(id, novelId);
      setSeries(prev => prev ? {
        ...prev,
        novels: prev.novels.filter(n => n.id !== novelId),
        novel_count: prev.novel_count - 1
      } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '移除失敗');
    }
  };

  // 拖拽排序
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null || dragOverIndex === null || !series || !id) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // 重新排序
    const novels = [...series.novels];
    const [movedNovel] = novels.splice(draggedIndex, 1);
    novels.splice(dragOverIndex, 0, movedNovel);

    // 樂觀更新
    setSeries({ ...series, novels });
    setDraggedIndex(null);
    setDragOverIndex(null);

    // 同步到後端
    try {
      await api.reorderSeriesNovels(id, novels.map(n => n.id));
    } catch (err) {
      // 還原
      await loadSeries();
      setError(err instanceof Error ? err.message : '排序失敗');
    }
  };

  // 計算總字數
  const totalWords = series?.novels.reduce((sum, n) => {
    return sum + (n.crawled_chapters * 3000);
  }, 0) || 0;

  const formatWordCount = (count: number) => {
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)} 萬字`;
    }
    return `${count} 字`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="w-12 h-12 border-3 border-neutral-200 dark:border-neutral-700 rounded-full" />
          <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-t-primary-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error && !series) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-danger-600 dark:text-danger-400 mb-4">{error}</div>
          <button
            onClick={() => navigate('/series')}
            className="btn btn-ghost text-primary-500"
          >
            返回系列列表
          </button>
        </div>
      </div>
    );
  }

  if (!series) return null;

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {/* 返回連結 */}
        <Link
          to="/series"
          className="inline-flex items-center gap-1.5 text-neutral-600 dark:text-neutral-400 hover:text-primary-500 mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          返回系列列表
        </Link>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-card">
            {error}
          </div>
        )}

        {/* Header Card */}
        <div className="card overflow-hidden mb-6">
          <div className="md:flex">
            {/* 封面 */}
            <div className="md:w-48 flex-shrink-0 bg-neutral-100 dark:bg-neutral-800">
              <CoverImage
                src={series.cover_url}
                alt={series.title}
                className="w-full h-48 md:h-full object-cover"
                fallback={
                  <div className="w-full h-48 md:h-64 flex items-center justify-center">
                    <div className="relative w-24 h-32 flex items-center justify-center">
                      {series.novel_count > 0 ? (
                        [...Array(Math.min(series.novel_count, 4))].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-6 h-full rounded-sm shadow-md"
                            style={{
                              left: `calc(50% - 18px + ${i * 10}px)`,
                              backgroundColor: `hsl(30, ${15 - i * 3}%, ${68 - i * 6}%)`
                            }}
                          />
                        ))
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-neutral-300 dark:bg-neutral-600 flex items-center justify-center">
                          <span className="text-xl text-white font-bold">
                            {series.title.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                }
              />
            </div>

            {/* 資訊 */}
            <div className="flex-1 p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="heading-page">
                    {series.title}
                  </h1>
                  {series.author && (
                    <p className="body-base mt-1">
                      作者：{series.author}
                    </p>
                  )}
                </div>

                {/* Admin 按鈕 */}
                {isAdmin && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setShowEditor(true)}
                      className="btn btn-ghost btn-icon"
                      title="編輯系列"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={handleDeleteSeries}
                      disabled={isDeleting}
                      className="btn btn-ghost btn-icon text-danger-600 dark:text-danger-400"
                      title="刪除系列"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {series.description && (
                <p className="mt-4 body-base whitespace-pre-wrap">
                  {series.description}
                </p>
              )}

              {/* 統計 */}
              <div className="mt-4 flex flex-wrap gap-4 body-small">
                <span className="inline-flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  {series.novel_count} 本小說
                </span>
                <span className="text-neutral-300 dark:text-neutral-600">·</span>
                <span>約 {formatWordCount(totalWords)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 小說列表 */}
        <div className="card overflow-hidden">
          <div className="card-header flex items-center justify-between">
            <h2 className="heading-section">
              系列內小說
            </h2>
            {isAdmin && (
              <button
                onClick={() => setShowAddNovel(true)}
                className="btn btn-primary btn-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                加入小說
              </button>
            )}
          </div>

          {series.novels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="body-base">這個系列還沒有小說</p>
              {isAdmin && (
                <button
                  onClick={() => setShowAddNovel(true)}
                  className="mt-4 btn btn-ghost text-primary-500"
                >
                  加入第一本小說
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {series.novels.map((novel, index) => (
                <NovelRow
                  key={novel.id}
                  novel={novel}
                  index={index}
                  isAdmin={isAdmin}
                  isDragging={draggedIndex === index}
                  isDragOver={dragOverIndex === index}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onRemove={() => handleRemoveNovel(novel.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <SeriesEditorModal
        series={series}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveSeries}
      />

      <AddNovelToSeriesModal
        seriesId={id!}
        existingNovelIds={series.novels.map(n => n.id)}
        isOpen={showAddNovel}
        onClose={() => setShowAddNovel(false)}
        onAdd={handleAddNovel}
      />
    </div>
  );
}

// 小說列表項元件
interface NovelRowProps {
  novel: Novel;
  index: number;
  isAdmin: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onRemove: () => void;
}

function NovelRow({
  novel,
  index,
  isAdmin,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove
}: NovelRowProps) {
  return (
    <div
      draggable={isAdmin}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={`flex items-center gap-4 p-4 transition-colors ${
        isDragging ? 'bg-primary-50 dark:bg-primary-900/20 opacity-50' : ''
      } ${isDragOver ? 'bg-primary-100 dark:bg-primary-900/30' : ''} ${
        isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
    >
      {/* 拖拽把手 */}
      {isAdmin && (
        <div className="text-neutral-400 flex-shrink-0">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      )}

      {/* 序號 */}
      <span className="w-6 text-center text-sm font-medium text-neutral-400 flex-shrink-0">
        {index + 1}
      </span>

      {/* 封面縮圖 */}
      <div className="w-12 h-16 flex-shrink-0 bg-neutral-100 dark:bg-neutral-700 rounded overflow-hidden">
        <CoverImage
          src={novel.cover_url}
          alt={novel.title}
          className="w-full h-full object-cover"
          fallback={
            <div className="w-full h-full flex items-center justify-center text-neutral-400 text-lg font-bold">
              {novel.title.charAt(0)}
            </div>
          }
        />
      </div>

      {/* 小說資訊 */}
      <div className="flex-1 min-w-0">
        <Link
          to={`/novels/${novel.id}`}
          className="font-medium text-neutral-900 dark:text-neutral-50 hover:text-primary-500 transition-colors"
        >
          {novel.title}
        </Link>
        <div className="text-sm text-neutral-500 dark:text-neutral-400">
          {novel.author || '未知作者'} · {novel.crawled_chapters} 章
        </div>
      </div>

      {/* 移除按鈕 */}
      {isAdmin && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="btn btn-ghost btn-icon btn-sm text-neutral-400 hover:text-danger-500"
          title="從系列移除"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
